// Guest share-link session for the web SPA.
//
// A guest arrives via https://trips.waagewu.com/g/{token}. GuestLanding
// validates the token against GET /api/guest/trips and persists it here
// (localStorage, `jg.guestTokens`) together with the trip ids it covers.
// From then on:
//   - api.ts request() + trip-state.ts attach `X-JG-Guest-Token` on
//     /api/trips/{tripId}/... calls via guestTokenForApiPath().
//   - AuthGate (App.tsx) consults guestSessionFor() to render trip
//     routes in guest mode instead of bouncing to SignInScreen.
//
// Tokens are persisted even for signed-in visitors: the Worker's
// requireTripRead prefers real membership, so a stored token next to a
// member cookie is harmless — and it keeps working if they sign out.

import { createContext, useContext } from 'react';

const STORE_KEY = 'jg.guestTokens';

interface StoredGuestLink {
  token: string;
  tripIds: string[];
  fetchedAt: string;
}

function readStore(): StoredGuestLink[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is StoredGuestLink =>
        !!e && typeof e.token === 'string' && Array.isArray(e.tripIds),
    );
  } catch {
    return [];
  }
}

function writeStore(entries: StoredGuestLink[]): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(entries));
  } catch {
    // Private-mode storage failures degrade to per-page-load guest
    // sessions; the landing re-validates on each visit anyway.
  }
}

export function addGuestToken(token: string, tripIds: string[]): void {
  const rest = readStore().filter((e) => e.token !== token);
  writeStore([
    ...rest,
    { token, tripIds, fetchedAt: new Date().toISOString() },
  ]);
}

export function removeGuestToken(token: string): void {
  writeStore(readStore().filter((e) => e.token !== token));
}

export function guestTokenForTrip(tripId: string): string | null {
  const hit = readStore().find((e) => e.tripIds.includes(tripId));
  return hit ? hit.token : null;
}

export function listGuestTokens(): string[] {
  return readStore().map((e) => e.token);
}

export function hasGuestTokens(): boolean {
  return readStore().length > 0;
}

// /api/trips/{tripId}/... (or bare /api/trips/{tripId}) → stored token.
// The path builders encodeURIComponent the tripId, so decode before the
// coverage lookup.
export function guestTokenForApiPath(path: string): string | null {
  const m = /^\/api\/trips\/([^/?]+)/.exec(path);
  if (!m) return null;
  try {
    return guestTokenForTrip(decodeURIComponent(m[1]));
  } catch {
    return null;
  }
}

// SPA location → guest surface? /g/{token} always is (the landing
// validates the token itself); /trip/{tripId} — and the decision short
// link /d/{tripId}/{decisionId}, which redirects into /trip/{tripId}/
// map-mode — only when a stored token covers that trip; the bare
// homepage when any token is stored — an anonymous guest's `/` is the
// read-only trips list fed from their grant (AuthGate only consults
// this for anonymous sessions there, so a signed-in owner's homepage
// is untouched).
export function guestSessionFor(
  pathname: string,
): { tripId: string | null } | null {
  if (/^\/g\/[a-f0-9]{32,128}$/i.test(pathname)) return { tripId: null };
  if (pathname === '/' && hasGuestTokens()) return { tripId: null };
  const t = /^\/(?:trip|d)\/([^/]+)/.exec(pathname);
  if (t) {
    try {
      const tripId = decodeURIComponent(t[1]);
      if (guestTokenForTrip(tripId)) return { tripId };
    } catch {
      return null;
    }
  }
  return null;
}

export interface GuestSession {
  isGuest: boolean;
  tripId: string | null;
  // True when the whole app session is anonymous (the auth probe 401'd) —
  // as opposed to a signed-in member viewing a shared trip they're not a
  // member of. Drives the sign-in affordances in guest chrome: only an
  // anonymous visitor should be offered "Sign in".
  anonymous: boolean;
}

export const GuestContext = createContext<GuestSession>({
  isGuest: false,
  tripId: null,
  anonymous: false,
});

export function useGuest(): GuestSession {
  return useContext(GuestContext);
}
